import { MASCOT, type MascotPose } from "./mascot";

/**
 * The words on the cards for the group chat (/card, views/Card.tsx, decisions 46 and 48), and the
 * title they share with the first slide. True to RULES, in Abhi's voice. No venue anywhere (his
 * call, 16 Oct). The hours are the hall's, 1pm to 6pm (his call, 16 Oct); round 1 is still at 1:15
 * and the last whistle at 5:45 inside them.
 */
export const TITLE = {
  name: "BDayminton",
  sub: "Abhi's 27th birthday badminton",
  when: "Sunday 18 October · 1pm to 6pm",
  year: "2026",
} as const;

/** The site, small along the bottom of every card. */
export const SITE = "bdayminton.technoid.cc";

/** Which ground a card stands on, as on the slides: the accent or a court colour. */
export type CardGround = "accent" | "court-1" | "court-2" | "court-4";

export interface GroupCard {
  /** For the picture's file name. */
  id: string;
  ground: CardGround;
  pose: MascotPose;
  /** What the host says on it, in a bubble over him. */
  says: string;
  /** The heading under a short title band; the first card has the whole title instead. */
  heading?: string;
  /** Short rows, one each beside the host (Card.css fits about 22 characters to a row). */
  lines?: readonly string[];
  /** The day as time and what happens, the slides' timeline in short (about 16 characters of what). */
  times?: readonly (readonly [string, string])[];
  /**
   * A poster instead of rows and a bubble, after the old recruiting posters (his ask, 16 Oct): his
   * name across the top, him big in the middle pointing out at whoever looks, the call under him in
   * the biggest letters, then `says` as a line, a short strip of `lines` if there are any, and the day.
   */
  poster?: { name: string; call: string };
}

/** The first card: the day in seven pointers. */
export const CARD_LINES: readonly string[] = [
  "Get there for 1pm",
  "Scan the QR code",
  "New partner each game",
  "First to 11, win by 2",
  "Ranked on your average",
  "Heckle, curse and pick",
  "Awards at the end",
];

/** What the host says on the first card. */
export const CARD_SAYS = `I am ${MASCOT.name}. On Sunday I am in charge. Most of you should practise.`;

/** His taunt about turning up, on How the day runs (his words, 16 Oct). */
export const ON_TIME = "Please arrive on time. Unlike your backhand.";

/** Every card, in the order they go to the group. */
export const CARDS: readonly GroupCard[] = [
  { id: "how-it-works", ground: "accent", pose: "crown", says: CARD_SAYS, lines: CARD_LINES },
  {
    id: "the-day",
    ground: "court-2",
    pose: "calling",
    heading: "How the day runs",
    says: ON_TIME,
    times: [
      ["1pm", "Arrive, scan in"],
      ["1:15", "Round 1"],
      ["Rounds", "Every 15 minutes"],
      ["Lunch", "Abhi calls it"],
      ["5:45", "Last whistle"],
      ["Then", "Awards, podium"],
      ["6pm", "All done"],
    ],
  },
  {
    id: "what-to-bring",
    ground: "court-1",
    pose: "smug",
    heading: "What to bring",
    says: "Bring a spare racket. You will want something to blame.",
    lines: ["Your racket", "Spares if you have any", "Non-marking trainers", "A water bottle", "A charged phone"],
  },
];

/** What the host says on the drop-in card: plain and warm, not a taunt (his pick, 16 Oct). */
export const DROP_IN_SAYS = "Can't stay all day? Come for an hour. Every game counts.";

/**
 * A card apart from the three, for anyone who said they cannot make it (his ask, 16 Oct): the day is
 * made to drop in and out of. Share all leaves it out; it has its own Save and Share.
 */
export const DROP_IN: GroupCard = {
  id: "drop-in",
  ground: "court-4",
  pose: "point",
  says: DROP_IN_SAYS,
  poster: { name: MASCOT.name, call: "needs you" },
  lines: ["Drop in, drop out", "Play one game or ten", "Leave when you like"],
};

/**
 * The rule cards (his ask, 18 Oct): two sets of four, each card on its own colour with the host in
 * his own mood, to send as albums. The morning set goes to the group before the day; the after the
 * intro set once everyone has been through the slides. Every row is what RULES.md says.
 */
export const MORNING: readonly GroupCard[] = [
  {
    id: "getting-in",
    ground: "court-1",
    pose: "point",
    heading: "Getting in",
    says: "Join at home. It will be the quickest thing you do all day.",
    lines: ["Scan the QR code", "Tap your name", "Set a 4-number PIN", "Add a photo of you", "Nickname, if you like", "I'm here, in the hall"],
  },
  {
    id: "a-game",
    ground: "court-2",
    pose: "whistle",
    heading: "A game",
    says: "One of you puts the score in. I will know if you lie.",
    lines: ["Doubles, four courts", "New partner each round", "First to 11, win by 2", "13 minutes at most", "Ahead at time wins", "Score in on the tablet"],
  },
  {
    id: "ranked",
    ground: "accent",
    pose: "crown",
    heading: "How you're ranked",
    says: "Resting costs you nothing. Your backhand costs you plenty.",
    lines: ["Average points a game", "Then win rate", "Resting costs nothing", "5 games for the podium", "Random pairs to start", "Then pairs by rank"],
  },
  {
    id: "coming-and-going",
    ground: "court-4",
    pose: "asleep",
    heading: "Coming and going",
    says: "Come and go as you like. The draw sorts it out. I sort out everything.",
    lines: ["Break? Tap On a break", "Back? Tap I'm back", "Late? Tap I'm here", "Off home? Tap Leaving", "Lunch: everyone stops", "Your average is safe"],
  },
];

export const AFTER_INTRO: readonly GroupCard[] = [
  {
    id: "your-phone",
    ground: "court-2",
    pose: "calling",
    heading: "Your phone",
    says: "It tells you where to be. Go there. Do not make me call your name.",
    lines: ["Now: your next game", "Court, partner, rivals", "Games: your record", "Your nemesis, if any", "Board: where you stand", "Your row at the top"],
  },
  {
    id: "while-you-rest",
    ground: "accent",
    pose: "smug",
    heading: "While you rest",
    says: "Curse whoever you like. I approve of every curse.",
    lines: ["Heckle on the wall", "One every 5 minutes", "Curse a pair, once", "Wrong hand, no smashes", "or Solo, 60 seconds", "Pick who wins, free"],
  },
  {
    id: "fun-points",
    ground: "court-4",
    pose: "nag",
    heading: "Fun points",
    says: "The main board is for badminton. The fun board is for everyone else.",
    lines: ["A second board", "1 point a right pick", "On a run: 2 each", "Beat the top three", "for 5, 3 or 2 points", "Win by 8: rename one"],
  },
  {
    id: "mini-games",
    ground: "court-1",
    pose: "shock",
    heading: "Mini games, awards",
    says: "I call the mini games. You play them. That is how being in charge works.",
    lines: ["Golden point at 10-10", "Quickfire: first to 7", "Twists: 90 seconds", "Then eight awards", "Then the podium"],
  },
];
